import { useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, ImageIcon, MapPin, Minus, Plus, ShoppingBag } from "lucide-react";
import { productImage } from "@/lib/api";
import { LOCATIONS } from "@/lib/constants";
import { LocationBadge } from "@/components/StatusBadge";
import ImageLightbox from "@/components/ImageLightbox";

const STATUS_QUICK = [
  { v: "all", l: "All" },
  { v: "not_purchased", l: "Not Purchased" },
  { v: "partial", l: "Partial" },
  { v: "purchased", l: "Purchased" },
];

function ShopCard({ p, canPurchase, onPurchase, qty, onQty, onZoom }) {
  const img = productImage(p);
  const variant = p.aliases?.length ? p.aliases.join(" · ") : null;
  return (
    <div data-testid={`shopping-item-${p.id}`} className="overflow-hidden rounded-md border bg-card">
      {img ? (
        <button
          type="button"
          data-testid={`shop-image-${p.id}`}
          aria-label={`View photo of ${p.name}`}
          className="block w-full active:opacity-90"
          onClick={() => onZoom({ src: img, alt: p.name })}
        >
          <img src={img} alt={p.name} className="h-44 w-full bg-stone-100 object-cover" />
        </button>
      ) : (
        <div className="flex h-24 w-full items-center justify-center bg-stone-100">
          <ImageIcon className="h-8 w-8 text-stone-300" />
        </div>
      )}
      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading text-lg font-bold leading-tight tracking-tight">{p.name}</p>
            {variant && <p className="mt-0.5 truncate text-xs font-semibold text-muted-foreground">{variant}</p>}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <LocationBadge location={p.buying_location} />
              {p.buffer_qty > 0 && (
                <span
                  data-testid={`buffer-badge-${p.id}`}
                  className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700"
                >
                  +{p.buffer_qty} buffer
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div data-testid={`need-to-buy-${p.id}`} className="font-heading text-5xl font-extrabold leading-none text-primary">
              {p.need_to_buy}
            </div>
            <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Need to buy</div>
          </div>
        </div>
        <p className="text-xs font-semibold text-stone-500">
          {p.purchased_qty}/{p.ordered_qty} purchased
        </p>
        {canPurchase && (
          <div className="flex items-stretch gap-2">
            <div className="flex items-center gap-1.5 rounded-md border bg-accent/40 p-1">
              <button
                type="button"
                data-testid={`shop-qty-minus-${p.id}`}
                aria-label="Decrease quantity"
                className="flex h-12 w-12 items-center justify-center rounded-md bg-white text-stone-700 shadow-sm transition-transform active:scale-95"
                onClick={() => onQty(p.id, qty - 1)}
              >
                <Minus className="h-5 w-5" />
              </button>
              <span data-testid={`shop-qty-value-${p.id}`} className="w-10 text-center font-heading text-xl font-extrabold">
                {qty}
              </span>
              <button
                type="button"
                data-testid={`shop-qty-plus-${p.id}`}
                aria-label="Increase quantity"
                className="flex h-12 w-12 items-center justify-center rounded-md bg-white text-stone-700 shadow-sm transition-transform active:scale-95"
                onClick={() => onQty(p.id, qty + 1)}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <button
              type="button"
              data-testid={`shopping-record-btn-${p.id}`}
              className="h-14 flex-1 rounded-md bg-primary font-heading text-base font-extrabold text-white transition-transform active:scale-[0.98]"
              onClick={() => onPurchase(p, qty)}
            >
              Bought {qty}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShoppingList({ products, canPurchase, onPurchase, location, onLocationChange, status, onStatusChange }) {
  const [completedOpen, setCompletedOpen] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [qtyMap, setQtyMap] = useState({});

  const qtyFor = (p) => qtyMap[p.id] ?? Math.max(1, p.need_to_buy);
  const setQtyFor = (id, v) => setQtyMap((m) => ({ ...m, [id]: Math.max(1, v) }));

  const locationOptions = useMemo(() => {
    const present = products.map((p) => p.buying_location).filter(Boolean);
    return [...new Set([...LOCATIONS, ...present])];
  }, [products]);

  const remainingByLocation = useMemo(() => {
    const m = { all: 0, unassigned: 0 };
    for (const p of products) {
      m.all += p.need_to_buy;
      const k = p.buying_location || "unassigned";
      m[k] = (m[k] || 0) + p.need_to_buy;
    }
    return m;
  }, [products]);

  const filtered = useMemo(
    () =>
      products.filter((p) => {
        if (location === "unassigned" && p.buying_location) return false;
        if (location !== "all" && location !== "unassigned" && p.buying_location !== location) return false;
        if (status !== "all" && p.status !== status) return false;
        return true;
      }),
    [products, location, status]
  );

  const active = filtered.filter((p) => p.need_to_buy > 0);
  const done = filtered.filter((p) => p.need_to_buy <= 0 && (p.ordered_qty > 0 || p.purchased_qty > 0));

  const groups = useMemo(() => {
    if (location !== "all") return [{ key: location, label: null, items: active }];
    const map = new Map();
    for (const p of active) {
      const k = p.buying_location || "Unassigned";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    return [...map.entries()]
      .sort((a, b) => (b[0] === "Unassigned") - (a[0] === "Unassigned") || a[0].localeCompare(b[0]))
      .map(([k, items]) => ({ key: k, label: k, items }));
  }, [active, location]);

  const totalNeed = active.reduce((s, p) => s + p.need_to_buy, 0);

  const chipCls = (activeChip, danger) =>
    `flex h-12 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors active:scale-[0.98] ${
      activeChip
        ? danger
          ? "border-red-500 bg-red-500 text-white"
          : "border-primary bg-primary text-white"
        : danger
          ? "border-red-200 bg-red-50 text-red-600"
          : "bg-card text-stone-600 hover:bg-accent"
    }`;

  const countBadge = (n, activeChip) =>
    n > 0 && (
      <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${activeChip ? "bg-white/25 text-white" : "bg-stone-100 text-stone-600"}`}>
        {n}
      </span>
    );

  return (
    <div data-testid="shopping-list" className="space-y-4">
      <div className="space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Buying location</p>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="shop-location-selector">
          <button data-testid="shop-location-all" onClick={() => onLocationChange("all")} className={chipCls(location === "all")}>
            All Locations {countBadge(remainingByLocation.all, location === "all")}
          </button>
          {locationOptions.map((l) => (
            <button
              key={l}
              data-testid={`shop-location-${l.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => onLocationChange(l)}
              className={chipCls(location === l)}
            >
              {l} {countBadge(remainingByLocation[l] || 0, location === l)}
            </button>
          ))}
          <button
            data-testid="shop-location-unassigned"
            onClick={() => onLocationChange("unassigned")}
            className={chipCls(location === "unassigned", true)}
          >
            Unassigned {countBadge(remainingByLocation.unassigned, location === "unassigned")}
          </button>
        </div>
      </div>

      <div className="flex min-w-0 gap-2 overflow-x-auto pb-1">
        {STATUS_QUICK.map((f) => (
          <button
            key={f.v}
            data-testid={`shop-filter-${f.v}`}
            onClick={() => onStatusChange(f.v)}
            className={`h-9 shrink-0 rounded-full border px-3.5 text-xs font-semibold transition-colors active:scale-[0.98] ${
              status === f.v ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <p className="text-base font-extrabold text-stone-800" data-testid="shopping-total-need">
        {totalNeed} units left to buy{location !== "all" ? ` at ${location === "unassigned" ? "UNASSIGNED" : location}` : ""}
      </p>

      {active.length === 0 && done.length === 0 ? (
        <div className="grid place-items-center rounded-md border border-dashed py-16 text-center">
          <div>
            <ShoppingBag className="mx-auto mb-3 h-10 w-10 text-stone-300" />
            <p className="font-medium text-muted-foreground" data-testid="shopping-empty">
              Nothing left to buy{location !== "all" ? ` at ${location === "unassigned" ? "UNASSIGNED" : location}` : ""}
            </p>
          </div>
        </div>
      ) : (
        <>
          {active.length === 0 && (
            <div className="grid place-items-center rounded-md border border-dashed py-10 text-center">
              <p className="font-medium text-muted-foreground" data-testid="shopping-empty">
                Nothing left to buy{location !== "all" ? ` at ${location === "unassigned" ? "UNASSIGNED" : location}` : ""}
              </p>
            </div>
          )}
          {groups.map((g) => (
            <div key={g.key} className="space-y-3">
              {g.label && (
                <div className="flex items-center gap-2 pt-1" data-testid={`shop-group-${g.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <MapPin className="h-4 w-4 text-primary" />
                  <p className="font-heading text-sm font-extrabold uppercase tracking-wide text-stone-700">{g.label}</p>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-bold text-stone-600">
                    {g.items.reduce((s, p) => s + p.need_to_buy, 0)} left
                  </span>
                </div>
              )}
              {g.items.map((p) => (
                <ShopCard
                  key={p.id}
                  p={p}
                  canPurchase={canPurchase}
                  onPurchase={onPurchase}
                  qty={qtyFor(p)}
                  onQty={setQtyFor}
                  onZoom={setZoomImg}
                />
              ))}
            </div>
          ))}

          {done.length > 0 && (
            <div className="rounded-md border bg-card">
              <button
                type="button"
                data-testid="show-completed-toggle"
                className="flex w-full items-center justify-between gap-2 p-4"
                onClick={() => setCompletedOpen((o) => !o)}
              >
                <span className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Completed ({done.length})
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${completedOpen ? "rotate-180" : ""}`} />
              </button>
              {completedOpen && (
                <div className="space-y-2 border-t p-3">
                  {done.map((p) => (
                    <div key={p.id} data-testid={`completed-item-${p.id}`} className="flex items-center gap-3 rounded-md border bg-accent/30 p-2.5">
                      {productImage(p) ? (
                        <img src={productImage(p)} alt={p.name} className="h-12 w-12 shrink-0 rounded-md border bg-stone-100 object-cover" />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                          <ImageIcon className="h-5 w-5 text-stone-300" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold">{p.name}</p>
                        <p className="text-xs font-semibold text-stone-500">
                          {p.purchased_qty}/{p.ordered_qty} purchased
                        </p>
                      </div>
                      <span className="shrink-0 rounded-md bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">Done</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {zoomImg && <ImageLightbox src={zoomImg.src} alt={zoomImg.alt} onClose={() => setZoomImg(null)} />}
    </div>
  );
}
